import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { 
  CreateReferrer, 
  FetchReferrers, 
  FetchReferrerTransactions,
  GetReferrerById,
  UpdateReferrerCode
} from "../actions/referrers";

interface Referrer {
  id: string;
  name: string;
  phone_number: string;
  code: string;
  referrer_type: string;
  discount: number;
  province: string;
  district: string;
  status: string;
  // Add other referrer properties as needed
}

type StatusType = "pending" | "success" | "error";

interface ReferrersState {
  list: any[];
  info: Record<string, any>;
  transactions: {
    list: any[];
    total: number;
    pageSize: number;
    currentPage: number;
    lastPage: number;
  };
  status: StatusType;
  error: string | undefined;
  fetchTimes: number;
  filters: Record<string, any>;
  currentReferrer: Referrer | null;
  currentReferrerStatus: StatusType;
  reports: {
    data: {
      total_referrers: {
        cumulative: {
          count: number;
        };
        current: {
          count: number;
        };
      };
      // Add other report metrics as needed
    };
    status: StatusType;
  };
}

const initialState: ReferrersState = {
  list: [],
  info: {},
  transactions: {
    list: [],
    total: 0,
    pageSize: 10,
    currentPage: 1,
    lastPage: 1
  },
  status: "pending",
  error: undefined,
  fetchTimes: 0,
  filters: {},
  currentReferrer: null,
  currentReferrerStatus: "pending",
  reports: {
    data: {
      total_referrers: {
        cumulative: {
          count: 0
        },
        current: {
          count: 0
        }
      }
    },
    status: "pending"
  }
};

const referrersSlice = createSlice({
  name: "referrers",
  initialState,
  reducers: {
    setFilters: (state, { payload }) => {
      state.filters = payload;
    },
    clearCurrentReferrer: (state) => {
      state.currentReferrer = null;
      state.currentReferrerStatus = "pending";
    },
  },
  extraReducers: (builder) => {
    builder
      // FetchReferrers cases
      .addCase(FetchReferrers.pending, (state) => {
        state.status = "pending";
        state.fetchTimes += 1;
      })
      .addCase(FetchReferrers.rejected, (state) => {
        state.status = "error";
      })
      .addCase(FetchReferrers.fulfilled, (state, { payload, meta: { arg } }) => {
        const { list, ...info } = payload;
        if (arg) state.filters = arg;
        state.status = "success";
        state.list = list;
        state.info = info;
      })

      // CreateReferrer cases
      .addCase(CreateReferrer.fulfilled, (state, action: PayloadAction<Referrer>) => {
        state.list.push(action.payload);
      })

     
     .addCase(FetchReferrerTransactions.fulfilled, (state, { payload }) => {
  state.transactions = {
    list: payload.list, // Flat array
    total: payload.total,
    currentPage: payload.pageNumber,
    lastPage: payload.lastPage,
    pageSize: payload.pageSize
  };
      })

      // GetReferrerById cases
      .addCase(GetReferrerById.pending, (state) => {
        state.currentReferrerStatus = "pending";
      })
      .addCase(GetReferrerById.fulfilled, (state, action: PayloadAction<Referrer>) => {
        state.currentReferrer = action.payload;
        state.currentReferrerStatus = "success";
        
        // Update the referrer in the list if it exists there
        const index = state.list.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(GetReferrerById.rejected, (state, action) => {
        state.currentReferrerStatus = "error";
        state.error = action.error.message || "Failed to fetch referrer";
      })

      // UpdateReferrerCode cases
      .addCase(UpdateReferrerCode.fulfilled, (state, action) => {
        if (state.currentReferrer) {
          state.currentReferrer.code = action.payload.referrer.code;
        }
        // Update in list if exists
        const index = state.list.findIndex(r => r.id === action.payload.referrer.id);
        if (index !== -1) {
          state.list[index].code = action.payload.referrer.code;
        }
      });
  },
});

export const { setFilters, clearCurrentReferrer } = referrersSlice.actions;
export default referrersSlice.reducer;